// UsLocationKeywordPage — programmatic "{Keyword} in {Location}" page.
// Route: /us/:location/:keyword   e.g. /us/california/ecommerce-marketing-agency
// Content is generated from usLocations.js × usKeywords.js.

import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronRight,
  Plus,
  Minus,
  MapPin,
  Building2,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import NotFoundPage from "./NotFoundPage";
import { getLocationBySlug, US_LOCATIONS, citiesInState, isIndexable } from "../data/usLocations";
import { getKeywordBySlug, US_KEYWORDS, fillCopy } from "../data/usKeywords";
import { Star, Quote, TrendingUp } from "lucide-react";

/* ─── FAQ Accordion ──────────────────────────────────────────── */
function FAQAccordion({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <ul className="border-t border-zinc-200">
      {items.map((f, idx) => {
        const isOpen = open === idx;
        return (
          <li key={f.q} className="border-b border-zinc-200">
            <button
              onClick={() => setOpen(isOpen ? -1 : idx)}
              className="w-full py-6 flex items-center justify-between gap-6 text-left group"
              aria-expanded={isOpen}
            >
              <span className="font-display text-lg md:text-xl font-bold text-charcoal group-hover:text-electric transition-colors">
                {f.q}
              </span>
              <span
                className={`shrink-0 w-9 h-9 grid place-items-center rounded-xl border transition-colors ${
                  isOpen
                    ? "bg-electric border-electric text-white"
                    : "border-zinc-200 text-charcoal group-hover:border-electric"
                }`}
              >
                {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              </span>
            </button>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.25 }}
                className="pb-6 pr-12"
              >
                <p className="text-zinc-600 leading-relaxed text-[15px]">{f.a}</p>
              </motion.div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/* ─── Main Page ──────────────────────────────────────────────── */
export default function UsLocationKeywordPage() {
  const { location: locSlug, keyword: kwSlug } = useParams();
  const loc = getLocationBySlug(locSlug);
  const kw = getKeywordBySlug(kwSlug);

  if (!loc || !kw) return <NotFoundPage />;

  const h1 = `${kw.name} in ${loc.name}`;
  const title = `${loc.name} ${kw.name} | Lumotis`;
  const desc = fillCopy(kw.tagline, loc).slice(0, 158);
  const pageUrl = `https://lumotis.com/us/${loc.slug}/${kw.slug}`;
  const locLabel = loc.type === "city" ? `${loc.name}, ${loc.stateAbbr}` : loc.name;

  const faq = kw.faq.map((f) => ({ q: fillCopy(f.q, loc), a: fillCopy(f.a, loc) }));
  const otherKeywords = US_KEYWORDS.filter((k) => k.slug !== kw.slug);
  const relatedKeywords = (kw.related || [])
    .map((s) => getKeywordBySlug(s))
    .filter(Boolean);

  // Other locations for this same keyword — prefer same-state cities, then a spread.
  const stateCities = loc.type === "state" ? citiesInState(loc.slug) : [];
  const sameStateSiblings =
    loc.type === "city" && loc.stateSlug
      ? US_LOCATIONS.filter((l) => l.slug !== loc.slug && (l.stateSlug === loc.stateSlug || l.slug === loc.stateSlug))
      : [];
  const nearbyPool = [...sameStateSiblings, ...US_LOCATIONS.filter((l) => l.slug !== loc.slug && !sameStateSiblings.includes(l))];
  const nearby = nearbyPool.slice(0, 8);

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={pageUrl} />
        {/* Tier-2 locations stay live for users but out of the index until enriched */}
        {!isIndexable(loc) && <meta name="robots" content="noindex, follow" />}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta name="geo.region" content={`US-${loc.stateAbbr}`} />
        <meta name="geo.placename" content={locLabel} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: h1,
            serviceType: kw.name,
            description: desc,
            provider: { "@type": "Organization", name: "Lumotis", url: "https://lumotis.com" },
            areaServed: {
              "@type": loc.type === "city" ? "City" : "State",
              name: loc.name,
              containedInPlace: { "@type": "Country", name: "United States" },
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://lumotis.com/" },
              { "@type": "ListItem", position: 2, name: "USA", item: "https://lumotis.com/us" },
              { "@type": "ListItem", position: 3, name: loc.name, item: `https://lumotis.com/us/${loc.slug}` },
              { "@type": "ListItem", position: 4, name: kw.name, item: pageUrl },
            ],
          })}
        </script>
      </Helmet>

      <Navbar />

      <main data-testid={`us-page-${loc.slug}-${kw.slug}`}>
        {/* ══════════════════════════════════════ HERO */}
        <section className="relative bg-white pt-28 md:pt-36 pb-20 md:pb-24 overflow-hidden border-b border-zinc-100">
          <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
          <div className="absolute right-0 top-0 w-2/3 h-2/3 bg-gradient-to-bl from-electric/8 via-transparent to-transparent pointer-events-none" />
          <div className="container relative">
            <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 flex-wrap mb-8">
              <Link to="/" className="hover:text-electric transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/us" className="hover:text-electric transition-colors">USA</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to={`/us/${loc.slug}`} className="hover:text-electric transition-colors">{loc.name}</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-charcoal">{kw.name}</span>
            </nav>

            <div className="grid lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <div className="inline-flex items-center gap-2.5 border border-electric/25 bg-electric/8 rounded-full px-4 py-1.5">
                    <span className="text-xl leading-none" aria-hidden>{kw.emoji}</span>
                    <span className="text-overline text-electric">{kw.name}</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-charcoal">
                    <span className="text-base leading-none" aria-hidden>{loc.emoji || "🇺🇸"}</span> {locLabel}
                  </span>
                </div>

                <motion.h1
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="font-display text-4xl sm:text-5xl lg:text-[56px] font-extrabold leading-[1.05] tracking-tight text-balance text-charcoal"
                >
                  {h1}
                </motion.h1>

                <p className="mt-4 font-display text-xl md:text-2xl font-bold leading-tight">
                  <span className="text-gradient">{kw.heroAccent}</span>
                </p>

                <p className="mt-6 text-lg text-zinc-500 leading-relaxed max-w-2xl">
                  {fillCopy(kw.tagline, loc)}
                </p>

                <div className="mt-9 flex flex-wrap items-center gap-3">
                  <Link
                    to="/seo-audit"
                    className="inline-flex items-center gap-2 bg-electric text-white px-6 py-3.5 font-semibold rounded-xl hover:bg-electric-500 hover:-translate-y-0.5 transition-all shadow-lg shadow-electric/30"
                  >
                    Get a Free {loc.name} Audit <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 bg-white border border-zinc-200 text-charcoal px-6 py-3.5 font-semibold rounded-xl hover:border-electric hover:text-electric transition-colors"
                  >
                    Talk to a Strategist <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-4 max-w-2xl">
                  {kw.stats.map((s) => (
                    <div key={s.label} className="rounded-2xl border border-zinc-100 bg-white px-4 py-4 shadow-card">
                      <p className="font-display text-2xl md:text-3xl font-extrabold text-charcoal">{s.value}</p>
                      <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1 leading-snug">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — local market card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="lg:col-span-5"
              >
                <div className="rounded-2xl bg-charcoal text-white p-8 shadow-glow-brand">
                  <p className="text-overline text-electric mb-3">{loc.type === "city" ? "City market" : "State market"}</p>
                  <p className="font-display text-5xl font-extrabold leading-none">{loc.stat.value}</p>
                  <p className="mt-2 text-zinc-400 text-sm">{loc.stat.label}</p>
                  <p className="mt-6 font-display text-lg font-bold leading-snug">{loc.tagline}</p>
                  <p className="mt-3 text-zinc-300 text-sm leading-relaxed">{loc.insight}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {loc.industries.map((ind) => (
                      <span key={ind} className="inline-flex items-center gap-1.5 border border-white/15 bg-white/5 rounded-full px-3 py-1 text-xs text-zinc-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-electric" /> {ind}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ WHAT WE DO */}
        <section className="py-20 md:py-28 bg-zinc-50 border-b border-zinc-100">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">01 — What we do</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  {kw.shortName} services for {loc.name} businesses.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                Six pillars we run for every {loc.name} client — coordinated, measured and tied to revenue.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {kw.pillars.map((p, idx) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.05 }}
                  className="group rounded-2xl border border-zinc-100 bg-white p-7 md:p-8 shadow-card hover:shadow-card-hover hover:border-electric/30 hover:-translate-y-1 transition-all"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="font-display font-extrabold text-2xl text-electric">0{idx + 1}</span>
                    <CheckCircle2 className="w-5 h-5 text-zinc-300 group-hover:text-electric transition-colors" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-charcoal group-hover:text-electric leading-snug mb-3 transition-colors">{p.title}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{fillCopy(p.body, loc)}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ LOCAL PROOF (Tier-1 enriched content) */}
        {loc.enriched && (
          <section className="py-20 md:py-28 bg-white border-b border-zinc-100" data-testid="local-proof">
            <div className="container">
              <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
                <div className="lg:col-span-8">
                  <p className="text-overline text-electric mb-3">Local expertise</p>
                  <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                    What makes {kw.shortName.toLowerCase()} different in {loc.name}.
                  </h2>
                </div>
                <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                  Every market has its own rules. Here's what we've learned running campaigns in {loc.name}.
                </p>
              </div>

              <div className="grid lg:grid-cols-12 gap-8">
                {/* Narrative + challenges */}
                <div className="lg:col-span-7">
                  <p className="text-[17px] text-zinc-700 leading-relaxed">{loc.enriched.narrative}</p>
                  <div className="mt-8">
                    <p className="text-xs uppercase tracking-widest font-bold text-zinc-400 mb-3">
                      {loc.name} challenges we solve
                    </p>
                    <ul className="space-y-2.5">
                      {loc.enriched.challenges.map((c) => (
                        <li key={c} className="flex gap-3 text-[15px] text-zinc-700">
                          <CheckCircle2 className="w-5 h-5 text-electric shrink-0 mt-0.5" />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-8 grid grid-cols-3 gap-3">
                    {loc.enriched.facts.map((f) => (
                      <div key={f.label} className="rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-4">
                        <p className="font-display text-2xl font-extrabold text-charcoal">{f.value}</p>
                        <p className="text-[11px] uppercase tracking-widest text-zinc-500 mt-1 leading-snug">{f.label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Case study + testimonial */}
                <div className="lg:col-span-5 flex flex-col gap-5">
                  <motion.article
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45 }}
                    className="rounded-2xl bg-charcoal text-white p-7 md:p-8 shadow-glow-brand"
                  >
                    <span className="inline-flex items-center gap-1.5 text-overline text-electric mb-4">
                      <TrendingUp className="w-3.5 h-3.5" /> {loc.enriched.caseStudy.badge}
                    </span>
                    <p className="font-display text-5xl md:text-6xl font-extrabold leading-none tracking-tight">{loc.enriched.caseStudy.metric}</p>
                    <p className="mt-2 text-zinc-400 text-xs uppercase tracking-widest">{loc.enriched.caseStudy.metricLabel}</p>
                    <h3 className="mt-6 font-display text-xl font-bold leading-snug">{loc.enriched.caseStudy.title}</h3>
                    <p className="mt-3 text-zinc-300 text-sm leading-relaxed">{loc.enriched.caseStudy.body}</p>
                  </motion.article>
                  <div className="rounded-2xl border border-zinc-100 bg-zinc-50 p-7 shadow-card">
                    <div className="flex items-center gap-0.5 mb-4">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-electric text-electric" />
                      ))}
                    </div>
                    <Quote className="w-6 h-6 text-electric/40 mb-2" />
                    <blockquote className="font-display text-lg font-bold text-charcoal leading-snug">
                      “{loc.enriched.testimonial.text}”
                    </blockquote>
                    <p className="mt-4 text-sm text-zinc-500">
                      — {loc.enriched.testimonial.name}, <span className="text-zinc-400">{loc.enriched.testimonial.role}</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ══════════════════════════════════════ AREAS WE SERVE */}
        <section className="py-20 md:py-28 bg-white border-b border-zinc-100">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">02 — Areas we serve</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                  {kw.shortName} across {loc.name}.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-500 leading-relaxed">
                {loc.type === "city"
                  ? `From ${loc.areas[0]} to ${loc.areas[loc.areas.length - 1]}, we serve businesses across ${loc.name} and the wider ${loc.stateName} market.`
                  : `We work with businesses in every major ${loc.name} market — and localise strategy to each.`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {loc.areas.map((a) => (
                <span key={a} className="inline-flex items-center gap-1.5 border border-zinc-200 bg-white rounded-full px-4 py-2 text-sm font-semibold text-charcoal">
                  <MapPin className="w-3.5 h-3.5 text-electric" /> {a}
                </span>
              ))}
            </div>

            {stateCities.length > 0 && (
              <div className="mt-10">
                <p className="text-xs uppercase tracking-widest font-bold text-zinc-400 mb-4">
                  {kw.name} pages for {loc.name} cities:
                </p>
                <div className="flex flex-wrap gap-2.5">
                  {stateCities.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/us/${c.slug}/${kw.slug}`}
                      className="inline-flex items-center gap-1.5 border border-electric/30 bg-electric/5 rounded-full px-4 py-2 text-sm font-semibold text-electric hover:bg-electric hover:text-white transition-colors"
                    >
                      <Building2 className="w-3.5 h-3.5" /> {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ══════════════════════════════════════ OTHER SERVICES IN LOCATION */}
        <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-[0.06] pointer-events-none" />
          <div className="container relative">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
              <div className="lg:col-span-8">
                <p className="text-overline text-electric mb-3">03 — More in {loc.name}</p>
                <h2 className="font-display text-3xl md:text-5xl font-extrabold text-balance leading-[1.05]">
                  Every marketing service we offer in {loc.name}.
                </h2>
              </div>
              <p className="lg:col-span-4 text-zinc-400 leading-relaxed">
                One agency, every channel — all coordinated for {loc.name} businesses.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {otherKeywords.map((k) => (
                <Link
                  key={k.slug}
                  to={`/us/${loc.slug}/${k.slug}`}
                  className="group flex items-center gap-3 border border-white/10 bg-white/5 rounded-xl px-4 py-3.5 hover:border-electric hover:bg-electric/10 transition-colors"
                >
                  <span className="text-xl" aria-hidden>{k.emoji}</span>
                  <span className="text-sm font-semibold text-zinc-200 group-hover:text-white flex-1">
                    {loc.name} {k.name}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:text-electric transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ FAQ */}
        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-100">
          <div className="container grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="text-overline text-electric mb-3">04 — FAQ</p>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold text-charcoal text-balance leading-[1.05]">
                {kw.shortName} in {loc.name} — common questions.
              </h2>
              <p className="mt-5 text-zinc-500 leading-relaxed">
                Have a different question? A real strategist replies within 24h.
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 bg-electric text-white px-6 py-3 font-semibold rounded-xl hover:bg-electric-500 transition-colors shadow-lg shadow-electric/25"
              >
                Ask the team <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-8">
              <FAQAccordion items={faq} />
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════ RELATED + NEARBY */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="grid lg:grid-cols-12 gap-12">
              <div className="lg:col-span-5">
                <p className="text-overline text-electric mb-3">05 — Related services</p>
                <h2 className="font-display text-2xl md:text-3xl font-extrabold text-charcoal leading-[1.05] mb-6">
                  Pairs well with {kw.shortName}.
                </h2>
                <div className="flex flex-col gap-3">
                  {relatedKeywords.map((r) => (
                    <Link
                      key={r.slug}
                      to={`/us/${loc.slug}/${r.slug}`}
                      className="group flex items-center gap-3 rounded-2xl border border-zinc-100 bg-white p-4 shadow-card hover:shadow-card-hover hover:border-electric/30 transition-all"
                    >
                      <span className="text-2xl" aria-hidden>{r.emoji}</span>
                      <span className="font-display font-bold text-charcoal group-hover:text-electric transition-colors flex-1">
                        {r.name} in {loc.name}
                      </span>
                      <ArrowUpRight className="w-4 h-4 text-zinc-300 group-hover:text-electric" />
                    </Link>
                  ))}
                  <Link
                    to={kw.serviceHref}
                    className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-electric hover:gap-3 transition-all"
                  >
                    <Sparkles className="w-4 h-4" /> Learn more about our {kw.shortName} service <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-7">
                <p className="text-overline text-electric mb-3">06 — Other locations</p>
                <h2 className="font-display text-2xl md:text-3xl font-extrabold text-charcoal leading-[1.05] mb-6">
                  {kw.name} in other US markets.
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {nearby.map((l) => (
                    <Link
                      key={l.slug}
                      to={`/us/${l.slug}/${kw.slug}`}
                      className="group rounded-xl border border-zinc-100 bg-white px-4 py-3 shadow-card hover:border-electric/30 hover:shadow-card-hover transition-all"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xl leading-none" aria-hidden>{l.emoji || "🏙️"}</span>
                        <span className="text-[10px] uppercase tracking-widest text-zinc-400">{l.type}</span>
                      </div>
                      <p className="font-display font-bold text-charcoal group-hover:text-electric transition-colors text-sm leading-snug">{l.name}</p>
                    </Link>
                  ))}
                </div>
                <Link
                  to="/us/locations"
                  className="mt-6 inline-flex items-center gap-2 border border-zinc-200 text-charcoal px-5 py-2.5 text-sm font-semibold rounded-xl hover:border-electric hover:text-electric transition-colors"
                >
                  View all US locations <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <CTABanner />
      </main>

      <Footer />
    </>
  );
}
